import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Briefcase, TrendingUp, ShoppingBag, BadgeCheck, Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { ArticleStrip } from "../components/ArticleStrip";
import { CareerQuiz } from "../components/CareerQuiz";
import { useAuth } from "../context/AuthContext";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessPage() {
  const [data, setData] = useState(null);
  const [inquiryFor, setInquiryFor] = useState(null);
  const [inquiry, setInquiry] = useState({ message: "", contact: "" });
  const { user, login } = useAuth();

  useEffect(() => {
    axios.get(`${API}/business`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const sendInquiry = async (m) => {
    if (!inquiry.message.trim()) return;
    try {
      await axios.post(`${API}/marketplace/inquiries`, {
        listing_id: m.id, listing_title: m.title, message: inquiry.message, contact: inquiry.contact,
      }, { withCredentials: true });
      toast.success("Inquiry sent — the provider will get back to you");
      setInquiryFor(null);
      setInquiry({ message: "", contact: "" });
    } catch {
      toast.error("Could not send inquiry — are you signed in?");
    }
  };

  return (
    <div>
      <PageHero topic="sports business stadium sponsorship" title="Sports Business, Economy & Careers" subtitle="The industry map behind world sport — clubs, media, venues, technology, careers and a marketplace for verified local services." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-12 px-4 py-12 sm:px-6">
        <AIContent topic="The global sports economy — how sport creates value and jobs" context="Editorial industry insight: revenues across media rights, sponsorship, matchday and merchandise; growth of women's sport, sports tech and emerging markets; community economic impact. Do not invent specific financial figures — speak in trends and structures." />
        <ArticleStrip tag="Business" title="Business Features" limit={3} />

        <section data-testid="industry-map">
          <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><TrendingUp className="h-6 w-6 text-[#0055FF]" /> <T>Industry Map</T></h2>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {data?.industry.map((s) => (
              <div key={s.id} data-testid={`industry-card-${s.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                <h3 className="text-base font-bold text-slate-900"><T>{s.name}</T></h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600"><T>{s.description}</T></p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.examples.map((e) => <span key={e} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[#0055FF]">{e}</span>)}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="careers-section">
          <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Briefcase className="h-6 w-6 text-purple-600" /> <T>Careers in Sport</T></h2>
          <div className="mt-5">
            {data && <CareerQuiz careers={data.careers} />}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="careers-grid">
            {data?.careers.map((c) => (
              <div key={c.id} data-testid={`career-card-${c.id}`} className="rounded-2xl border border-purple-100 bg-purple-50/50 p-6">
                <h3 className="text-base font-bold text-slate-900"><T>{c.title}</T></h3>
                <p className="mt-2 text-sm text-slate-600"><span className="font-bold text-slate-700"><T>Pathway</T>:</span> <T>{c.pathway}</T></p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.skills.map((s) => <span key={s} className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-purple-700 border border-purple-200">{s}</span>)}
                </div>
                <p className="mt-3 text-xs font-semibold text-purple-700"><T>Demand</T>: <T>{c.demand}</T></p>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="marketplace-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><ShoppingBag className="h-6 w-6 text-emerald-600" /> <T>Local Marketplace</T></h2>
            {user && <Link to="/inbox" data-testid="inbox-link" className="rounded-full border border-[#0055FF] px-4 py-2 text-sm font-bold text-[#0055FF] hover:bg-blue-50"><T>Inquiry Inbox</T> →</Link>}
          </div>
          <p className="mt-1 text-sm text-slate-500"><T>Tickets, lessons, camps, facilities and equipment from local providers. Listings are demo examples.</T></p>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data?.marketplace.map((m) => (
              <div key={m.id} data-testid={`marketplace-card-${m.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">{m.category}</span>
                  {m.verified && <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700"><BadgeCheck className="h-3 w-3" /><T>Verified</T></span>}
                  {m.sponsored && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700"><Megaphone className="h-3 w-3" /><T>Sponsored</T></span>}
                </div>
                <h3 className="mt-3 text-base font-bold text-slate-900"><T>{m.title}</T></h3>
                <p className="mt-1 text-sm text-slate-500">{m.location}</p>
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-sm font-extrabold text-[#0055FF]">{m.price}</span>
                  <span className="text-xs text-slate-500">{m.provider}</span>
                </div>
                {inquiryFor === m.id ? (
                  <div className="mt-3 space-y-2" data-testid={`inquiry-form-${m.id}`}>
                    <textarea data-testid={`inquiry-message-${m.id}`} value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} placeholder="Your question for the provider…" rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#0055FF]" />
                    <input data-testid={`inquiry-contact-${m.id}`} value={inquiry.contact} onChange={(e) => setInquiry({ ...inquiry, contact: e.target.value })} placeholder="Preferred contact (optional)" className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-[#0055FF]" />
                    <div className="flex gap-2">
                      <button data-testid={`inquiry-send-${m.id}`} onClick={() => sendInquiry(m)} className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] px-4 py-2 text-sm font-bold text-white hover:bg-[#0040CC]"><Send className="h-3.5 w-3.5" /> <T>Send inquiry</T></button>
                      <button onClick={() => setInquiryFor(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-slate-500"><T>Cancel</T></button>
                    </div>
                  </div>
                ) : user ? (
                  <button data-testid={`inquiry-open-${m.id}`} onClick={() => { setInquiryFor(m.id); setInquiry({ message: "", contact: "" }); }} className="mt-3 w-full rounded-full border border-[#0055FF] px-4 py-2 text-sm font-bold text-[#0055FF] hover:bg-blue-50"><T>Contact provider</T></button>
                ) : (
                  <button data-testid={`inquiry-login-${m.id}`} onClick={login} className="mt-3 w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-slate-500 hover:border-[#0055FF] hover:text-[#0055FF]"><T>Sign in to contact provider</T></button>
                )}
              </div>
            ))}
          </div>
          <p className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="sponsorship-disclosure">
            <T>Transparency: sponsored placements are always labeled and never change verification status, results, rankings, safety guidance or editorial conclusions.</T>
          </p>
        </section>
      </div>
    </div>
  );
}
